import { useEffect, useRef } from "react";

/**
 * Poll ``callback`` every ``ms`` milliseconds — but only when the browser tab
 * is currently visible. Uses a ref so that a changing ``callback`` identity
 * does NOT reset the interval (which would cause polling drift, especially
 * on frequent state updates in the consumer).
 *
 * Extras:
 *   * Immediately re-fires ``callback`` on ``visibilitychange`` from hidden → visible
 *     so users returning to the tab see fresh data without waiting up to ``ms``.
 *   * Cleans up both timer and event listener on unmount.
 *
 * The callback is invoked with no arguments; consumers should close over any
 * needed state.
 */
export function useVisibleInterval(callback, ms) {
  const cbRef = useRef(callback);
  // Keep the ref in sync every render — avoids `callback` in the deps array.
  cbRef.current = callback;

  useEffect(() => {
    if (!ms || ms <= 0) return undefined;
    const tick = () => {
      if (document.visibilityState === "visible") cbRef.current();
    };
    const onVis = () => {
      if (document.visibilityState === "visible") cbRef.current();
    };
    const id = setInterval(tick, ms);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [ms]);
}
